import { useMemo } from "react"

import type {
	FeedbackSearchResult,
	FeedbackWithRelations,
} from "@productlane/zero/queries"

// A preview is one line, so a body past this is cut before any parser sees it.
// Some synced emails carry megabytes of HTML, and every visible row parses one.
const PREVIEW_SOURCE_LIMIT = 8 * 1024

function getLastMessage<T>(
	messages: readonly T[] | undefined,
	messageSorting: "desc" | "asc",
): T | undefined {
	if (!messages || messages.length === 0) return undefined
	return messageSorting === "desc" ? messages[0] : messages[messages.length - 1]
}

function getLastMatchingMessage<T>(
	messages: readonly T[] | undefined,
	messageSorting: "desc" | "asc",
	predicate: (m: T) => boolean,
): T | undefined {
	if (!messages || messages.length === 0) return undefined
	if (messageSorting === "desc") {
		return messages.find(predicate)
	}
	for (let i = messages.length - 1; i >= 0; i--) {
		const m = messages[i]
		if (m !== undefined && predicate(m)) return m
	}
	return undefined
}

interface PreviewAttachment {
	name?: string
	type?: string
}

function normalizeAttachment(raw: unknown): PreviewAttachment | undefined {
	let value = raw
	if (typeof value === "string") {
		try {
			value = JSON.parse(value)
		} catch {
			return undefined
		}
	}
	if (!value || typeof value !== "object") return undefined
	const a = value as Record<string, unknown>
	const name = [a.name, a.Name].find((v) => typeof v === "string" && v.trim())
	const type = [a.type, a.contentType, a.ContentType].find(
		(v) => typeof v === "string" && v.trim(),
	)
	if (!name && !type) return undefined
	return { name: name as string | undefined, type: type as string | undefined }
}

// Slack, Teams and email all send image-only messages with an empty body, and a
// row with no preview text reads as an empty thread.
function attachmentPreviewText(raw: unknown): string | undefined {
	if (!Array.isArray(raw)) return undefined
	const items = raw
		.map(normalizeAttachment)
		.filter((a): a is PreviewAttachment => !!a)
	if (items.length === 0) return undefined
	if (items.length > 1) return `${items.length} attachments`
	const only = items[0]
	if (only?.name) return only.name
	return only?.type?.startsWith("image/") ? "Image" : "Attachment"
}

interface MessageCandidate {
	createdAt: number
	text: string
	authorName?: string
	isTeamReply?: boolean
	isAi?: boolean
}

function extractFirstName(name: string | undefined | null) {
	if (!name) return undefined
	const trimmed = name.trim()
	if (!trimmed) return undefined
	return trimmed.split(/\s+/)[0]
}

function emailFromName(from: unknown): string | undefined {
	if (!from || typeof from !== "object") return undefined
	const f = from as { name?: unknown; email?: unknown }
	if (typeof f.name === "string" && f.name.trim()) return f.name
	if (typeof f.email === "string") return f.email.split("@")[0]
	return undefined
}

function useLastMessageCandidate(
	thread: FeedbackWithRelations | FeedbackSearchResult | undefined,
	messageSorting: "desc" | "asc",
) {
	return useMemo(() => {
		const candidates: MessageCandidate[] = []

		const lastSlackMessage = getLastMessage(
			thread?.slackMessages,
			messageSorting,
		)
		const slackText =
			lastSlackMessage?.html ||
			attachmentPreviewText(lastSlackMessage?.attachments)
		if (lastSlackMessage && slackText) {
			candidates.push({
				createdAt: lastSlackMessage.createdAt,
				text: slackText,
				isTeamReply: !!lastSlackMessage.userId,
			})
		}

		const lastEmail = getLastMatchingMessage(
			thread?.emails,
			messageSorting,
			(e) => e.source !== "AUTO_REPLY",
		)
		if (lastEmail) {
			// The list queries carry only the stored plain-text preview, never the body.
			const emailText =
				lastEmail.preview.slice(0, PREVIEW_SOURCE_LIMIT).trim() ||
				attachmentPreviewText(lastEmail.attachments)
			if (emailText) {
				candidates.push({
					createdAt: lastEmail.createdAt,
					text: emailText,
					authorName: emailFromName(lastEmail.from),
					isTeamReply: !!lastEmail.userId && lastEmail.source !== "AI",
					isAi: lastEmail.source === "AI",
				})
			}
		}

		const lastThreadMessage = getLastMessage(
			thread?.threadMessages,
			messageSorting,
		)
		if (lastThreadMessage?.text) {
			candidates.push({
				createdAt: lastThreadMessage.createdAt,
				text: lastThreadMessage.text,
				authorName: lastThreadMessage.actorName,
			})
		}

		const lastChatMessage = getLastMessage(thread?.chatMessages, messageSorting)
		const chatText =
			lastChatMessage?.html ||
			attachmentPreviewText(lastChatMessage?.attachments)
		if (lastChatMessage && chatText) {
			candidates.push({
				createdAt: lastChatMessage.createdAt,
				text: chatText,
				authorName: lastChatMessage.authorName ?? undefined,
				isTeamReply: !!lastChatMessage.userId,
			})
		}

		const lastLiveMessage = getLastMessage(
			thread?.liveChatSession?.messages,
			messageSorting,
		)
		const liveText =
			lastLiveMessage?.content ||
			attachmentPreviewText(lastLiveMessage?.attachments)
		if (lastLiveMessage && liveText) {
			candidates.push({
				createdAt: lastLiveMessage.createdAt,
				text: liveText,
				authorName: lastLiveMessage.name ?? undefined,
				isTeamReply: lastLiveMessage.role === "AGENT",
				isAi: lastLiveMessage.role === "AI",
			})
		}

		if (candidates.length === 0) return undefined

		return candidates.reduce((latest, current) =>
			current.createdAt > latest.createdAt ? current : latest,
		)
	}, [
		messageSorting,
		thread?.liveChatSession?.messages,
		thread?.emails,
		thread?.slackMessages,
		thread?.chatMessages,
		thread?.threadMessages,
	])
}

export function useLastThreadMessage(
	thread: FeedbackWithRelations | FeedbackSearchResult | undefined,
	messageSorting: "desc" | "asc",
) {
	const candidate = useLastMessageCandidate(thread, messageSorting)
	return (candidate?.text ?? "").slice(0, PREVIEW_SOURCE_LIMIT)
}

export function useLastThreadMessageAuthorFirstName(
	thread: FeedbackWithRelations | FeedbackSearchResult | undefined,
	messageSorting: "desc" | "asc",
) {
	const candidate = useLastMessageCandidate(thread, messageSorting)
	return extractFirstName(candidate?.authorName)
}

export function useLastThreadMessageIsTeamReply(
	thread: FeedbackWithRelations | FeedbackSearchResult | undefined,
	messageSorting: "desc" | "asc",
) {
	const candidate = useLastMessageCandidate(thread, messageSorting)
	return candidate?.isTeamReply ?? false
}

export function useLastThreadMessageAuthorLabel(
	thread: FeedbackWithRelations | FeedbackSearchResult | undefined,
	messageSorting: "desc" | "asc",
) {
	const candidate = useLastMessageCandidate(thread, messageSorting)
	if (candidate?.isAi) return "Productlane Agent"
	return extractFirstName(candidate?.authorName)
}
